import { Injectable, signal, computed } from '@angular/core';
import { Dealer } from '@app/_models/dealer';
import { DealerService } from './dealer-service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DealerStateService {
  // Private state signals
  private readonly _dealer = signal<Dealer | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _currentDealerId = signal<string | null>(null);

  // Public readonly signals
  readonly dealer = this._dealer.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly currentDealerId = this._currentDealerId.asReadonly();

  // Prevent concurrent loads
  private loadingPromise: Promise<void> | null = null;

  // Computed signals
  readonly hasDealer = computed(() => this._dealer() !== null);
  readonly dealerBalance = computed(() => this._dealer()?.dealerBalance ?? 0);
  readonly dealerPolicyBalance = computed(() => this._dealer()?.dealerPolicyBalance ?? 0);
  readonly dealerPackageBalance = computed(() => {
    const dealer = this._dealer();
    if (!dealer) return 0;
    return (dealer.dealerBalance || 0) + (dealer.dealerPolicyBalance || 0);
  });

  constructor(private dealerService: DealerService) {}

  /**
   * Load dealer data by dealerId and update the shared state
   * Prevents concurrent loads - if already loading the same dealer, returns existing promise
   * @param dealerId - The dealer ID to load
   */
  async loadDealer(dealerId: string): Promise<void> {
    // If dealer is already loaded and matches, no need to reload
    const currentDealer = this._dealer();
    const currentDealerId = this._currentDealerId();
    if (currentDealer && currentDealer.dealerId === dealerId && currentDealerId === dealerId && !this._loading()) {
      return Promise.resolve();
    }

    // If already loading the same dealer, return the existing promise
    if (this.loadingPromise && currentDealerId === dealerId) {
      return this.loadingPromise;
    }

    // Start new load - set loading state FIRST to prevent race conditions
    this._currentDealerId.set(dealerId);
    this._loading.set(true);
    this._error.set(null);

    this.loadingPromise = (async () => {
      try {
        const dealer = await firstValueFrom(this.dealerService.getByDealerId(dealerId));
        this._dealer.set(dealer);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load dealer';
        this._error.set(errorMessage);
        throw err;
      } finally {
        this._loading.set(false);
        this.loadingPromise = null;
      }
    })();

    return this.loadingPromise;
  }

  /**
   * Refresh dealer data from backend and update shared state
   * Forces a fresh load bypassing the cache to ensure latest data
   */
  async refreshDealer(): Promise<void> {
    const dealerId = this._currentDealerId();
    if (!dealerId) {
      throw new Error('No dealer ID available. Call loadDealer first.');
    }
    
    // Set loading state to bypass cache in loadDealer
    this._loading.set(true);
    this._error.set(null);
    
    try {
      const dealer = await firstValueFrom(this.dealerService.getByDealerId(dealerId));
      this._dealer.set(dealer);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh dealer';
      this._error.set(errorMessage);
      throw err;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Update dealer in shared state (without backend call)
   * Useful when you know the dealer has been updated
   */
  updateDealer(dealer: Dealer): void {
    this._dealer.set(dealer);
  }

  /**
   * Optimistic update pattern - update local state immediately, then sync with backend
   */
  async updateDealerOptimistic(updates: Partial<Dealer>): Promise<void> {
    const currentDealer = this._dealer();
    if (!currentDealer) {
      throw new Error('No dealer loaded. Call loadDealer first.');
    }

    // Optimistic update
    const updatedDealer = { ...currentDealer, ...updates };
    this._dealer.set(updatedDealer);

    try {
      // Sync with backend
      await firstValueFrom(this.dealerService.update(currentDealer.id, updates));
    } catch (err) {
      // Rollback on failure
      this._dealer.set(currentDealer);
      throw err;
    }
  }

  /**
   * Clear the current dealer state
   */
  clearDealer(): void {
    this._currentDealerId.set(null);
    this._dealer.set(null);
    this._error.set(null);
  }
}
